import React from 'react';
import { useNavigate } from 'react-router-dom';

const EFFECTIVE_DATE = 'March 1, 2026';
const COMPANY = 'RAMDESIGNWORKS';
const SITE = 'theoperatormethod.com';
const EMAIL = 'support@theoperatormethod.com';
const PHONE = '(216) 916-6869';
const ADDRESS = '815 Superior Ave. East, Suite 1618, Cleveland, OH 44114';

export default function TermsPage() {
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#0a0e1a',
      fontFamily: "'Inter', -apple-system, sans-serif",
      color: '#f0f4ff',
    }}>
      {/* Nav */}
      <div style={{
        padding: '20px 40px',
        borderBottom: '1px solid #1e2a3a',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '11px', letterSpacing: '0.25em', fontWeight: 700 }}
        >
          THE OPERATOR METHOD
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{ background: 'none', border: '1px solid #1e2a3a', color: '#6b7a99', cursor: 'pointer', fontSize: '13px', padding: '6px 14px', borderRadius: '6px' }}
        >
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: '760px', margin: '0 auto', padding: '60px 24px 80px' }}>
        <p style={{ fontSize: '11px', letterSpacing: '0.25em', color: '#4f8ef7', textTransform: 'uppercase', marginBottom: '16px' }}>Legal</p>
        <h1 style={{ fontSize: '32px', fontWeight: 700, margin: '0 0 8px', color: '#f0f4ff' }}>Terms of Service</h1>
        <p style={{ color: '#4a5568', fontSize: '14px', marginBottom: '48px' }}>Effective date: {EFFECTIVE_DATE}</p>

        <Section title="1. Agreement to Terms">
          <p>By accessing or purchasing from The Operator Method at <strong>{SITE}</strong>, you agree to be bound by these Terms of Service. These Terms constitute a legally binding agreement between you and <strong>{COMPANY}</strong> ("we," "us," or "our"). If you do not agree, do not use the site.</p>
        </Section>

        <Section title="2. The Service">
          <p>The Operator Method is an online AI training course delivered through our web platform. We offer three access tiers:</p>
          <ul style={listStyle}>
            <li><strong>Foundations</strong> — Access to Modules 0 and 1</li>
            <li><strong>Full Operator</strong> — Access to all 5 modules</li>
            <li><strong>Certified Operator</strong> — Access to all 5 modules plus a certification credential upon completion</li>
          </ul>
          <p>Course content is delivered digitally. No physical materials are shipped.</p>
        </Section>

        <Section title="3. Accounts">
          <p>You must create an account to access the course. You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account. You must provide accurate information at registration. One account per person — accounts are non-transferable.</p>
          <p>We reserve the right to suspend or terminate accounts that violate these Terms.</p>
        </Section>

        <Section title="4. Payments & Pricing">
          <p>All prices are listed in US dollars. Payments are processed securely through Stripe and/or PayPal. By completing a purchase, you authorize the charge to your selected payment method.</p>
          <p>Access to your purchased tier is granted immediately upon confirmed payment. Prices may change at any time; changes do not affect purchases already made.</p>
        </Section>

        <Section title="5. Refund Policy">
          <p>Due to the immediate digital delivery of course content, all sales are final. We do not offer refunds once access to course materials has been granted.</p>
          <p>If you experience a technical issue that prevents you from accessing content you purchased, contact us at <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a> and we will work to resolve it promptly.</p>
          <p>If a charge was made in error (e.g., a duplicate transaction), contact us within 7 days and we will investigate and issue a correction where appropriate.</p>
        </Section>

        <Section title="6. Intellectual Property">
          <p>All course content — including text, lesson materials, exercises, prompts, code, design, and the AI entity visual identity — is owned by {COMPANY} or its licensors and is protected by copyright law.</p>
          <p>Your purchase grants you a personal, non-transferable, non-exclusive license to access the course materials for your own learning. You may not:</p>
          <ul style={listStyle}>
            <li>Copy, reproduce, or distribute course materials</li>
            <li>Share your account credentials with others</li>
            <li>Resell, sublicense, or create derivative works from the content</li>
            <li>Use course content for commercial training of others without written permission</li>
          </ul>
        </Section>

        <Section title="7. Acceptable Use">
          <p>You agree not to use the platform or any of its AI-powered features to:</p>
          <ul style={listStyle}>
            <li>Violate any applicable local, state, national, or international law or regulation</li>
            <li>Engage in or facilitate fraudulent, deceptive, or misleading activity of any kind</li>
            <li>Collect, harvest, or exploit the personal data or private information of any individual without their explicit consent</li>
            <li>Harass, threaten, defame, or cause harm to any person</li>
            <li>Attempt to reverse-engineer, scrape, or automate access to the platform</li>
            <li>Interfere with or disrupt the service or its infrastructure</li>
            <li>Impersonate another person or entity</li>
            <li>Submit false or misleading information</li>
          </ul>
          <p><strong>AI Feature Restrictions.</strong> The AI tools and conversational features provided through this platform are intended solely for lawful educational and professional development purposes. Use of these features for illegal activity, fraud, privacy violations, or any purpose that causes harm to others is strictly prohibited.</p>
          <p><strong>User Responsibility.</strong> You are solely responsible for all content you submit to and all interactions you conduct through the platform, including your use of any AI-powered features. We are not liable for any outcomes, decisions, or actions taken based on AI-generated responses.</p>
          <p><strong>Enforcement.</strong> Violations of this Acceptable Use policy — including any prohibited use of AI features — may result in immediate suspension or permanent termination of your account without refund, and may be referred to appropriate legal authorities where warranted.</p>
          <p><strong>Security Doctrine.</strong> All AI security enforcement mechanisms on this platform — including input pattern detection, session monitoring, browser fingerprinting, and account bans — are governed by the <a href="/security-doctrine" style={linkStyle}>AI Security Doctrine, Version 1.0</a>, effective {EFFECTIVE_DATE}. All security enforcement mechanisms derive from that doctrine. Violations detected by those mechanisms are subject to the enforcement protocol defined therein.</p>
        </Section>

        <Section title="8. Disclaimers">
          <p>The Operator Method provides education and training. We do not guarantee specific outcomes, results, income, or employment from completing the course. Results depend on individual effort and application.</p>
          <p>The course is provided "as is" without warranty of any kind, express or implied, including warranties of merchantability or fitness for a particular purpose.</p>
        </Section>

        <Section title="9. Limitation of Liability">
          <p>To the fullest extent permitted by law, {COMPANY} shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of your use of or inability to use the service.</p>
          <p>Our total liability to you for any claim arising from these Terms or your use of the service shall not exceed the amount you paid for your access tier in the twelve months preceding the claim.</p>
        </Section>

        <Section title="10. Governing Law">
          <p>These Terms are governed by the laws of the State of <strong>Ohio</strong>, United States, without regard to its conflict of law provisions. Any disputes shall be resolved in the courts of Ohio.</p>
        </Section>

        <Section title="11. Changes to Terms">
          <p>We reserve the right to update these Terms at any time. We will update the effective date when we do. Continued use of the service after changes constitutes acceptance of the updated Terms.</p>
        </Section>

        <Section title="12. Contact">
          <p>
            {COMPANY}<br />
            {ADDRESS}<br />
            Phone: <a href={`tel:${PHONE.replace(/[^0-9]/g, '')}`} style={linkStyle}>{PHONE}</a><br />
            Email: <a href={`mailto:${EMAIL}`} style={linkStyle}>{EMAIL}</a><br />
            Website: <a href={`https://${SITE}`} style={linkStyle}>{SITE}</a>
          </p>
        </Section>

        <div style={{ borderTop: '1px solid #1e2a3a', paddingTop: '32px', marginTop: '48px', display: 'flex', gap: '24px' }}>
          <button onClick={() => navigate('/privacy')} style={{ background: 'none', border: 'none', color: '#4f8ef7', cursor: 'pointer', fontSize: '14px', padding: 0 }}>Privacy Policy →</button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: '40px' }}>
      <h2 style={{ fontSize: '18px', fontWeight: 600, color: '#f0f4ff', margin: '0 0 16px', paddingBottom: '8px', borderBottom: '1px solid #1e2a3a' }}>{title}</h2>
      <div style={{ color: '#94a3b8', lineHeight: 1.8, fontSize: '15px' }}>{children}</div>
    </div>
  );
}

const listStyle = { paddingLeft: '20px', lineHeight: 2, margin: '8px 0' };
const linkStyle = { color: '#4f8ef7', textDecoration: 'none' };
